import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, gridDateComparator, gridNumberComparator } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { DateFormatPreset, NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SupplierRelationshipType } from '@/generated/gql/graphql';
import { useConfig } from '~/components/ConfigProvider';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  dateFormatterFactory,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
  numberFormatterFactory,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { CommodityLinkCell } from '~/components/DataGridCustom/cells/CommodityLinkCell/CommodityLinkCell';
import { CuriosityLinkChipCell } from '~/components/DataGridCustom/cells/CuriosityLinkChip/CuriosityLinkChipCell';
import { CuriosityLocationCell } from '~/components/DataGridCustom/cells/CuriosityLocationCell/CuriosityLocationCell';
import { CuriositySourceReferenceCell } from '~/components/DataGridCustom/cells/CuriositySourceReferenceCell/CuriositySourceReferenceCell';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { IconId } from '~/components/Icon';
import { useModuleConfig } from '~/components/ModuleConfigProvider';
import { RouteId } from '~/routes';

import { DemandRequestWidgetConfig } from './DemandRequestWidget.config';
import {
  DemandRequestLineItemGridColumnGroup,
  DemandRequestLineItemGridField,
  DemandRequestLineItemGridRow,
} from './DemandRequestWidget.types';

const relationshipColorMap: { [key in SupplierRelationshipType]?: string } = {
  [SupplierRelationshipType.known]: 'success.main',
  [SupplierRelationshipType.partner]: 'secondary.main',
};
export const statusColorFromRelationship = (value: SupplierRelationshipType): string | undefined => {
  return value ? relationshipColorMap[value] : undefined;
};

export const useDemandRequestGridConfig: UseDataGridConfig<DemandRequestLineItemGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'DemandRequestLineItemGrid.column' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'DemandRequestLineItemGrid.group' });
  const widgetConfig = useModuleConfig<DemandRequestWidgetConfig>();
  const { features } = useConfig();
  return useMemo<DataGridConfig<DemandRequestLineItemGridRow>>(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      columnVisibilityModel: {
        [DemandRequestLineItemGridField.PREFERRED_SUPPLIER_ID]: !widgetConfig.lineItems?.preferredSupplierId?.hidden,
        [DemandRequestLineItemGridField.ORGANIZATION]: features.showOrganization,
        [DemandRequestLineItemGridField.DELIVERY_LOCATION]: features.showDeliveryLocation,
      },
      columns: [
        GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        createColumn({
          field: DemandRequestLineItemGridField.POSITION,
          minWidth: 60,
          hideable: false,
          renderCell: cellFactory(EmptyFallbackCell),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.TITLE,
          minWidth: 200,
          renderCell: cellFactory(EmptyFallbackCell),
        }),

        createColumn({
          field: DemandRequestLineItemGridField.QUANTITY,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.DECIMAL }),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: DemandRequestLineItemGridField.UNIT,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.UNIT,
          }),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.EXPECTED_PRICE_PER_UNIT,
          minWidth: 150,
          renderCell: cellFactory(CurrencyCell),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.BASELINE_PRICE_PER_UNIT,
          minWidth: 150,
          renderCell: cellFactory(CurrencyCell),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.COMMODITY,
          minWidth: 200,
          renderCell: cellFactory(CommodityLinkCell, ({ row }) => ({
            to: RouteId.commodityDetail(row.commodityId),
            needs: {
              permission: Permission.BaseCommodityList,
            },
          })),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.COMMODITY_GROUP_ID,
          minWidth: 200,
          renderCell: cellFactory(CuriosityLinkChipCell, ({ row }) => ({
            i18nKey: row.commodityGroupId,
            ns: CuriosityNamespaceType.COMMODITY_GROUP,
          })),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.ORGANIZATION,
          minWidth: 300,
          renderCell: cellFactory(CuriosityValueCell, ({ row }) => ({
            i18nKey: row.commodityGroupId,
            ns: CuriosityNamespaceType.ORGANIZATION,
          })),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.DELIVERY_LOCATION,
          minWidth: 350,
          renderCell: cellFactory(CuriosityLocationCell),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.CONTRACT_ITEM,
          minWidth: 200,
          renderCell: cellFactory(CuriosityValueCell, ({ row }) => ({
            i18nKey: row.contractItemId,
            ns: CuriosityNamespaceType.CONTRACT_ITEM,
          })),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.PREFERRED_SUPPLIER_ID,
          minWidth: 200,
          renderCell: cellFactory(CuriositySourceReferenceCell, {
            ns: CuriosityNamespaceType.SUPPLIER,
            includeSourceReference: true,
          }),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.FROM,
          valueGetter: (_value, row) => row.shipmentTerms?.earlyDeliveryAt,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 200,
        }),
        createColumn({
          field: DemandRequestLineItemGridField.TO,
          valueGetter: (_value, row) => row.shipmentTerms?.lateDeliveryAt,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 200,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: DemandRequestLineItemGridColumnGroup.REQUEST_ITEM,
          renderHeaderGroup: headerGroupWithIcon(IconId.demandRequestItem),
          children: [
            //
            { field: DemandRequestLineItemGridField.POSITION },
            { field: DemandRequestLineItemGridField.TITLE },
            { field: DemandRequestLineItemGridField.COMMODITY_GROUP_ID },
            { field: DemandRequestLineItemGridField.CONTRACT_ITEM },
            { field: DemandRequestLineItemGridField.UNIT },
            { field: DemandRequestLineItemGridField.QUANTITY },
            { field: DemandRequestLineItemGridField.EXPECTED_PRICE_PER_UNIT },
            { field: DemandRequestLineItemGridField.PREFERRED_SUPPLIER_ID },
            { field: DemandRequestLineItemGridField.ORGANIZATION },
            { field: DemandRequestLineItemGridField.DELIVERY_LOCATION },
            { field: DemandRequestLineItemGridField.COMMODITY },
            { field: DemandRequestLineItemGridField.BASELINE_PRICE_PER_UNIT },
          ],
        }),
        createGroup({
          groupId: DemandRequestLineItemGridColumnGroup.DELIVERY_DATE,
          renderHeaderGroup: headerGroupWithIcon(IconId.deliveryDate),
          children: [
            //
            { field: DemandRequestLineItemGridField.FROM },
            { field: DemandRequestLineItemGridField.TO },
          ],
        }),
      ],
    };
  }, [t, tColumn, tGroup, widgetConfig, features.showOrganization, features.showDeliveryLocation]);
};
